import { createInstance, isBrowserSupported, SessionState } from "@fra.me/terminal-factory";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { clearCookie, getCookie, setCookie } from "./cookies.js";
import { getRequiredElement } from "./dom.js";
import type { LatencyResult } from "./je-latency.js";
import pingRegion from "./je-latency.js";
import type { NoticesJson } from "./notifications.js";
import { dismissNotices, hideAwsNotification, showNotices } from "./notifications.js";
import type { SessionDependencies } from "./session.js";
import { checkUntilClosed, loadFrame, sessionStateString, startSession } from "./session.js";
import { setSentryLocation, setSentryUser } from "./telemetry.js";
import { getToken } from "./token.js";
import { toggleStartButton, updateYear } from "./ui.js";
import { clearLocalStorage, getEnumKeyByEnumValue } from "./utils.js";

/**
 * App Core
 * Copyright 2025 ENERCALC, LLC
 * All rights reserved.
 *
 * Shared bootstrap and orchestration for both the production (`index.html` /
 * `app.ts`) and internal test (`test.html` / `test-app.ts`) entry points. Each
 * entry runs `Sentry.init` with its own options, builds an {@link AppConfig},
 * and calls {@link bootstrap}. All login/launch/region orchestration lives here
 * so the two sites cannot drift.
 *
 * @author: ENERCALC, LLC
 */

// Terminal class is not exported from @fra.me/terminal-factory — derive type from createInstance.
export type FrameTerminal = Awaited<ReturnType<typeof createInstance>>;

/** LOGIN / FRAME_GQL endpoint URLs (shape shared by config.ts and test-config.ts). */
interface AppApiEndpoints {
  readonly LOGIN: string;
  readonly FRAME_GQL: string;
}

/** Credential cookie names (shape shared by config.ts and test-config.ts). */
interface AppCookieNames {
  readonly KWNUMBER: string;
  readonly PASSWORD: string;
  readonly USERNAME: string;
}

/** A single region's Frame account config (structurally matches config.ts's FrameAccountConfig). */
interface AppFrameAccountConfig {
  readonly terminalConfigId: string;
  readonly applicationId: string;
}

/**
 * Fields shared by both {@link AppConfig} variants. Sentry options are
 * intentionally NOT here — `Sentry.init` runs in each entry file before
 * `bootstrap` (see the entry files).
 */
interface AppConfigBase {
  /** Region label -> AWS region id (e.g. `{ NOVA: "us-east-1" }`). */
  awsRegionNames: Readonly<Record<string, string>>;
  apiEndpoints: AppApiEndpoints;
  cookieNames: AppCookieNames;
  frameAccountConfigs: Readonly<Record<string, AppFrameAccountConfig>>;
  /**
   * When set, {@link browserDetection} writes this message into `#browserAdvisory`
   * before revealing it. Production omits it (its markup is pre-rendered); the test
   * site provides it because its `#browserAdvisory` span starts empty.
   */
  browserAdvisoryMessage?: string;
}

/**
 * Everything {@link bootstrap} needs that genuinely differs between the
 * production and test sites.
 *
 * A discriminated union on `notificationBanner`: when the banner is enabled
 * (production), `notices` is REQUIRED — the banner cannot render without it, so
 * making it mandatory turns a would-be silent dead-banner into a compile error.
 * When disabled (test site), it is not permitted.
 */
export type AppConfig =
  | (AppConfigBase & {
      notificationBanner: true;
      /** Notices payload rendered into the banner. */
      notices: NoticesJson;
    })
  | (AppConfigBase & {
      notificationBanner: false;
      notices?: undefined;
    });

/**
 * Returns the highest-priority (lowest latency) region whose value appears
 * as an `<option>` in the given Set of available region IDs.
 *
 * Regions present in AwsRegionNames but absent from the `<select>` (e.g. NORCAL,
 * SYDNEY) are excluded so the app cannot auto-select a region the user cannot
 * actually choose.
 *
 * @param sortedResults - Latency results sorted ASC by latency.
 * @param availableIds - Region value strings from the `<select>` options.
 * @returns The lowest-latency available region, or null if none found.
 */
export function pickLowestLatencyAvailableRegion(
  sortedResults: LatencyResult[],
  availableIds: Set<string>
): LatencyResult | null {
  const selectable = sortedResults.filter((r) => availableIds.has(r.id));
  return selectable.length > 0 ? selectable[0] : null;
}

/**
 * Captures a session-launch failure to Sentry, tagging the Frame SDK
 * `TerminalError` code when present so failures are filterable by SDK error
 * code (e.g. 5404 SESSION_IN_INVALID_STATE, which the SDK returns for both
 * capacity exhaustion and a still-closing prior session). The SDK message is
 * attached as extra context, not a tag — tags are for low-cardinality indexed
 * values, and the message is already on the captured exception.
 *
 * Errors without a numeric `code` (network failures, thrown non-Error values)
 * are captured unchanged with no added tags.
 *
 * @param error - The caught launch error (typed `unknown` at the catch site).
 * @returns void
 */
export function captureLaunchError(error: unknown): void {
  const code = error && typeof error === "object" && "code" in error ? error.code : undefined;

  if (typeof code === "number") {
    const message =
      "message" in (error as object) ? (error as { message: unknown }).message : undefined;
    Sentry.withScope((scope) => {
      scope.setTag("frame_error_code", String(code));
      if (typeof message === "string") {
        scope.setExtra("frame_error_message", message);
      }
      Sentry.captureException(error);
    });
    return;
  }

  Sentry.captureException(error);
}

/**
 * Detects whether the current browser is supported. When it is not, reveals the
 * browser advisory and disables the launch button.
 *
 * @param browserAdvisory - The advisory element shown for unsupported browsers.
 * @param startButton - The launch button, disabled when the browser is unsupported.
 * @param advisoryMessage - Optional message to write into the advisory before
 *   revealing it. Production omits this (its markup is pre-rendered, so it stays
 *   byte-identical); the test site passes it because its advisory element is empty.
 * @returns True if the browser is supported, false otherwise.
 */
export function browserDetection(
  browserAdvisory: HTMLElement,
  startButton: HTMLButtonElement,
  advisoryMessage?: string
): boolean {
  if (isBrowserSupported()) {
    return true;
  }
  if (advisoryMessage) {
    browserAdvisory.innerHTML = advisoryMessage;
  }
  browserAdvisory.classList.remove("hidden");
  toggleStartButton(startButton, false, "Launch");
  return false;
}

/** Dependencies injected into {@link startApp}. */
interface StartAppDeps {
  selectedLocationInput: HTMLSelectElement;
  startButton: HTMLButtonElement;
  errorContainer: HTMLElement;
  sessionDeps: SessionDependencies;
  setTerminal: (terminal: FrameTerminal) => void;
  /** Records the auto-selected region (the bootstrap-scoped `selectedLocation` seam). */
  setSelectedLocation: (value: string) => void;
  /** Region label -> id map from the active {@link AppConfig}. */
  awsRegionNames: Readonly<Record<string, string>>;
  /** Frame account configs from the active {@link AppConfig}. */
  frameAccountConfigs: Readonly<Record<string, AppFrameAccountConfig>>;
}

/**
 * Probes every region's latency, picks the lowest-latency region that is
 * actually selectable in the `<select>`, updates the option labels, selects it,
 * and loads the Frame session for it.
 *
 * @param token - Frame token.
 * @param deps - Injected DOM elements, session dependencies, config, and state seams.
 * @returns void
 */
export async function startApp(token: string, deps: StartAppDeps): Promise<void> {
  const {
    selectedLocationInput,
    startButton,
    errorContainer,
    sessionDeps,
    setTerminal,
    setSelectedLocation,
    awsRegionNames,
    frameAccountConfigs,
  } = deps;
  const latencyResults: Array<Promise<LatencyResult>> = [];

  // ping each region and push the results to latencyResults array
  Object.entries(awsRegionNames).forEach(([, value]) => {
    const promise = pingRegion(value);
    if (promise) latencyResults.push(promise);
  });

  const results = await Promise.all(latencyResults);

  // sort the results by latency
  results.sort((a, b) => a.latency - b.latency);

  // Build the set of region IDs that are actually present in the <select>.
  // This is the canonical authoritative set — regions in AwsRegionNames but absent
  // from the <select> (NORCAL / us-west-1) must not be auto-selected.
  const availableOptionValues = new Set(
    Array.from(selectedLocationInput.options)
      .filter((opt) => opt.value !== "" && !opt.disabled)
      .map((opt) => opt.value)
  );

  const regionWithLowestLatency = pickLowestLatencyAvailableRegion(results, availableOptionValues);

  if (!regionWithLowestLatency) {
    console.warn("startApp: no selectable regions available in <select>; skipping auto-selection");
    toggleStartButton(startButton, true, "Launch");
    return;
  }

  const options = Array.from(selectedLocationInput.options);

  // update the labels with the latency
  options.forEach((option) => {
    const regionInfo = results.find((r) => r.id === option.value);
    if (regionInfo === undefined) return;
    if (regionWithLowestLatency.id === regionInfo.id) {
      option.selected = true;
    }
    option.label = `${option.dataset.name} (${regionInfo.latency} ms)`;
  });

  // start with the region with the lowest latency selected
  const selected = regionWithLowestLatency.id;
  setSelectedLocation(selected);
  setSentryLocation(options.find((option) => option.value === selected));

  const account = getEnumKeyByEnumValue(awsRegionNames, selected);
  if (account && frameAccountConfigs[account]) {
    await loadFrame(frameAccountConfigs[account], token, sessionDeps, setTerminal);
  } else {
    console.warn("Unknown region selected in startApp:", selected);
    errorContainer.innerText = "Unknown location selected. Please refresh and try again.";
    toggleStartButton(startButton, true, "Launch");
  }
}

/** Dependencies injected into {@link validateCredentials}. */
interface ValidateCredentialsDeps {
  /**
   * Reads the current Frame terminal (null when no session exists yet).
   * A getter, not a snapshot, so the value stays live across awaits and the
   * CLOSING-session recursion — matching the original closure's live read.
   */
  getTerminal: () => FrameTerminal | null;
  startButton: HTMLButtonElement;
  statusMessage: HTMLElement;
  kwInput: HTMLInputElement;
  kwPasswordInput: HTMLInputElement;
  userNameInput: HTMLInputElement;
  errorContainer: HTMLElement;
  sessionDeps: SessionDependencies;
  /**
   * Reads the region the user has selected. A getter, not a snapshot, so a
   * location change during the login/CLOSING-wait window is reflected — matching
   * the original closure's live read of the bootstrap-scoped `selectedLocation`.
   */
  getSelectedLocation: () => string;
  handleError: (error: unknown, message?: string) => void;
  /** LOGIN endpoint from the active {@link AppConfig}. */
  apiEndpoints: AppApiEndpoints;
  /** Credential cookie names from the active {@link AppConfig}. */
  cookieNames: AppCookieNames;
  /** Region label -> id map from the active {@link AppConfig}. */
  awsRegionNames: Readonly<Record<string, string>>;
  /** Frame account configs from the active {@link AppConfig}. */
  frameAccountConfigs: Readonly<Record<string, AppFrameAccountConfig>>;
}

/**
 * Validates the user's credentials and starts or resumes a Frame session.
 *
 * Resumes an OPEN session, waits out a CLOSING session (then retries), or logs
 * the user in via the LOGIN endpoint and starts a fresh session for the selected
 * region. A no-op when no terminal exists yet.
 *
 * @param deps - Injected DOM elements, session dependencies, config, and current state.
 * @returns A promise that resolves when the credentials have been processed.
 */
export async function validateCredentials(deps: ValidateCredentialsDeps): Promise<void> {
  const {
    getTerminal,
    startButton,
    statusMessage,
    kwInput,
    kwPasswordInput,
    userNameInput,
    errorContainer,
    sessionDeps,
    getSelectedLocation,
    handleError,
    apiEndpoints,
    cookieNames,
    awsRegionNames,
    frameAccountConfigs,
  } = deps;

  const terminal = getTerminal();
  if (!terminal) {
    return;
  }

  const session = (await terminal.getOpenSession()) as Awaited<
    ReturnType<typeof terminal.getOpenSession>
  > | null;
  const sessionState = session ? sessionStateString(session) : undefined;
  if (session && sessionState === SessionState.OPEN) {
    await terminal.resume(session.id);
    toggleStartButton(startButton, true, "Resuming...");
  } else if (session && sessionState === SessionState.CLOSING) {
    // Wait for session to close before starting a new one
    toggleStartButton(startButton, false, "Waiting for session to close...");
    statusMessage.innerText = "Previous session is closing. Please wait...";
    const sessionClosed = await checkUntilClosed(sessionDeps);
    if (sessionClosed) {
      // Recursively call validateCredentials after session is closed
      await validateCredentials(deps);
    }
  } else {
    try {
      const response = await axios.post(
        apiEndpoints.LOGIN,
        `pLogin=${kwInput.value}&pPwd=${kwPasswordInput.value}`
      );
      const ologin = (response.data as { APIResult: number }).APIResult;
      if (ologin === 1) {
        errorContainer.textContent = "";
        setCookie(cookieNames.KWNUMBER, kwInput.value, 180);
        setCookie(cookieNames.PASSWORD, kwPasswordInput.value, 180);
        setCookie(cookieNames.USERNAME, userNameInput.value, 180);

        setSentryUser(kwInput, userNameInput);

        statusMessage.innerText = "Connection in progress";
        const selectedLocation = getSelectedLocation();
        const account = getEnumKeyByEnumValue(awsRegionNames, selectedLocation);
        if (account && frameAccountConfigs[account]) {
          await startSession(frameAccountConfigs[account].applicationId, sessionDeps);
        } else {
          console.warn("Unknown region selected in validateCredentials:", selectedLocation);
          errorContainer.innerText = "Unknown location selected. Please refresh and try again.";
        }
      } else {
        errorContainer.innerText =
          "Your KW number and/or password were not valid, were missing, or your company / agency required that we disable cloud access.";
      }
    } catch (error) {
      handleError(error, "Error validating credentials");
    }
  }
}

/**
 * Wires the login page: resolves the required DOM elements, restores saved
 * credentials, binds the location/submit/clear handlers, optionally renders the
 * production notification banner, and kicks off region latency detection.
 *
 * Registers its own `DOMContentLoaded` listener; each entry file calls
 * `bootstrap(config)` once at module top level.
 *
 * @param config - Site-specific configuration (production vs test).
 * @returns void
 */
export function bootstrap(config: AppConfig): void {
  // The DOMContentLoaded handler is async; addEventListener ignores the returned
  // promise. The handler's async work (token fetch, launch) is internally
  // try/caught, but its synchronous prologue — the getRequiredElement lookups —
  // can still throw if a required element is missing, which would surface as an
  // unhandled rejection. Attach .catch so any fatal bootstrap failure is reported
  // to Sentry instead of vanishing.
  document.addEventListener("DOMContentLoaded", () => {
    onDomReady().catch(captureLaunchError);
  });

  async function onDomReady(): Promise<void> {
    let terminal: FrameTerminal | null = null;
    let selectedLocation = "";

    const startButton = getRequiredElement<HTMLButtonElement>("btnlaunch");
    const clearButton = getRequiredElement<HTMLButtonElement>("btnClear");
    const statusMessage = getRequiredElement<HTMLElement>("loginCheck");
    const kwInput = getRequiredElement<HTMLInputElement>("kw_number");
    const kwPasswordInput = getRequiredElement<HTMLInputElement>("kw_password");
    const userNameInput = getRequiredElement<HTMLInputElement>("txt_username");
    const errorContainer = getRequiredElement<HTMLElement>("LoginErrorMessage");
    const selectedLocationInput = getRequiredElement<HTMLSelectElement>("vmlocation");
    const loginForm = getRequiredElement<HTMLFormElement>("loginForm");
    const loginFieldset = getRequiredElement<HTMLFieldSetElement>("loginFieldset");
    const browserAdvisory = getRequiredElement<HTMLElement>("browserAdvisory");
    const copyrightYear = getRequiredElement<HTMLElement>("copyrightYear");

    // The AWS notification banner is production-only markup. Gate its lookups
    // behind the config flag so the test site (which has no banner) never throws.
    let awsNotificationBanner: HTMLElement | null = null;
    let awsNotificationCloseBtn: HTMLElement | null = null;
    if (config.notificationBanner) {
      awsNotificationBanner = getRequiredElement<HTMLElement>("aws-notification-banner");
      // Intentionally nullable: the close button is a parent-scoped, class-based
      // lookup guarded with `if (awsNotificationCloseBtn)`.
      awsNotificationCloseBtn = awsNotificationBanner.querySelector(".notification-banner__close");
    }

    const kwnumber = getCookie(config.cookieNames.KWNUMBER);
    const userpass = getCookie(config.cookieNames.PASSWORD);
    const username = getCookie(config.cookieNames.USERNAME);

    /**
     * Handles errors by capturing them in Sentry and logging to the console.
     *
     * @param error - The error to handle.
     * @param message - Optional message to log with the error.
     * @returns void
     */
    const handleError = (error: unknown, message = "An error occurred"): void => {
      Sentry.captureException(error);
      console.warn(`ERROR: ${message}: ${String(error)}`);
    };

    if (kwnumber && userpass) {
      kwInput.value = kwnumber;
      kwPasswordInput.value = userpass;
      userNameInput.value = username ?? "";
      setSentryUser(kwInput, userNameInput);
    }

    // Prevent spaces from being used in the username field
    userNameInput.addEventListener("input", function () {
      this.value = this.value.replace(/\s+/g, "");
    });

    // Build dependencies object for session functions
    const sessionDeps: SessionDependencies = {
      startButton,
      statusMessage,
      loginFieldset,
      kwInput,
      kwPasswordInput,
      userNameInput,
      terminal: null,
      serviceUrl: config.apiEndpoints.FRAME_GQL,
    };

    const setTerminal = (newTerminal: FrameTerminal): void => {
      terminal = newTerminal;
      sessionDeps.terminal = newTerminal;
    };

    selectedLocationInput.addEventListener("change", (event) => {
      void (async (): Promise<void> => {
        const target = event.target as HTMLSelectElement;
        selectedLocation = target.value;
        setSentryLocation(target.options[target.selectedIndex]);
        try {
          const account = getEnumKeyByEnumValue(config.awsRegionNames, target.value);
          if (account && config.frameAccountConfigs[account]) {
            const token = await getToken();
            await loadFrame(config.frameAccountConfigs[account], token, sessionDeps, setTerminal);
          } else {
            console.warn("Unknown region selected in change handler:", target.value);
            errorContainer.innerText = "Unknown location selected. Please refresh and try again.";
          }
        } catch (error) {
          handleError(error, "Error changing location");
        }
      })();
    });

    // Validate form
    loginForm.addEventListener("submit", (event) => {
      event.preventDefault();

      // check form validity
      if (!loginForm.checkValidity()) {
        console.warn("User did not fill out all required fields");
        loginForm.reportValidity();
        return;
      }

      void validateCredentials({
        getTerminal: () => terminal,
        startButton,
        statusMessage,
        kwInput,
        kwPasswordInput,
        userNameInput,
        errorContainer,
        sessionDeps,
        getSelectedLocation: () => selectedLocation,
        handleError,
        apiEndpoints: config.apiEndpoints,
        cookieNames: config.cookieNames,
        awsRegionNames: config.awsRegionNames,
        frameAccountConfigs: config.frameAccountConfigs,
      });
    });

    // Clear browser cookies and local storage
    clearButton.addEventListener("click", () => {
      try {
        clearCookie(config.cookieNames.KWNUMBER);
        clearCookie(config.cookieNames.PASSWORD);
        clearCookie(config.cookieNames.USERNAME);
        clearLocalStorage();
        statusMessage.innerText = "Login information cleared";
        loginForm.reset();
      } catch (error) {
        handleError(error, "Error clearing cookies");
        errorContainer.innerText = "Error clearing login. Please reload the page and try again.";
      }
    });

    // Production-only notification banner wiring. The discriminated AppConfig
    // guarantees notices is present whenever notificationBanner is true, so no
    // extra presence checks are needed beyond the nullable close button.
    if (config.notificationBanner && awsNotificationBanner) {
      const banner = awsNotificationBanner;
      const { notices } = config;
      if (awsNotificationCloseBtn) {
        awsNotificationCloseBtn.addEventListener("click", () => {
          hideAwsNotification(banner);
          dismissNotices(notices);
        });
      }
      showNotices(notices, banner);
    }

    updateYear(copyrightYear);
    browserDetection(browserAdvisory, startButton, config.browserAdvisoryMessage);
    let frameToken: string | null = null;
    try {
      frameToken = await getToken();
    } catch (error) {
      handleError(error, "Error getting token");
    }
    if (!frameToken) {
      errorContainer.textContent =
        "We're having trouble connecting to the cloud platform. Please reload the page and try again. If the problem persists, please contact support.";
      return;
    }
    try {
      await startApp(frameToken, {
        selectedLocationInput,
        startButton,
        errorContainer,
        sessionDeps,
        setTerminal,
        setSelectedLocation: (value: string) => {
          selectedLocation = value;
        },
        awsRegionNames: config.awsRegionNames,
        frameAccountConfigs: config.frameAccountConfigs,
      });
    } catch (error) {
      captureLaunchError(error);
      console.warn("ERROR: Error starting app:", error);
      errorContainer.textContent =
        "We're having trouble starting a cloud session. Please reload the page and try again, or select a different location. If the problem persists, please contact support.";
    }
  }
}
